import { FC, ReactNode } from 'react';
import { styled } from '@mui/material';

import { BACKGROUND, GREEN, ORANGE, PEARL, PRIMARY, RED, TEXT } from '../../Helpers/colors';

export enum BranchTypes {
	'primary',
	'success',
}

export enum ClientStatusTypes {
	'primary', // Pending Insurance Authorization
	'success', // Active
	'error', // Paused
	'gray', // Discharged
	'solidGray', // All
}

export enum SoapNoteStatusTypes {
	'gray', // draft
	'success', // submitted
	'error', // expired bt
	'warning', // expired bcba
	'primary', // unlocked
	'solidWarning', // Pending BT
	'solidGray', // All
}

export enum TargetStatusTypes {
	'gray', // Ready
	'primary', // InTreatment/Reopened
	'success', // Mastered
	'warning', // OnHold
}

type SizeType = 'sm' | 'md' | 'lg' | 'xl';

export type BadgeTypes =
	| 'gray'
	| 'primary'
	| 'success'
	| 'warning'
	| 'error'
	| 'solidGray'
	| 'solidPrimary'
	| 'solidSuccess'
	| 'solidWarning'
	| 'solidError';

const heights = {
	sm: '24px',
	md: '28px',
	lg: '32px',
	xl: '36px',
};

const paddings = {
	sm: '4px 8px',
	md: '4px 10px',
	lg: '6px 12px',
	xl: '8px 16px',
};

export const colors = {
	gray: TEXT.SECONDARY,
	primary: PRIMARY.DARK[16],
	success: GREEN.DARK[16],
	warning: ORANGE.DARK[16],
	error: RED.DARK[16],
	solidGray: BACKGROUND.PRIMARY[100],
	solidPrimary: BACKGROUND.PRIMARY[100],
	solidSuccess: BACKGROUND.PRIMARY[100],
	solidWarning: BACKGROUND.PRIMARY[100],
	solidError: BACKGROUND.PRIMARY[100],
};

export const backgroundColors = {
	gray: PEARL.TERTIARY,
	primary: PRIMARY.TERTIARY,
	success: GREEN.QUATERNARY,
	warning: ORANGE.TERTIARY,
	error: RED.TERTIARY,
	solidGray: PEARL.BASE[700],
	solidPrimary: PRIMARY.BASE,
	solidSuccess: GREEN.BASE,
	solidWarning: ORANGE.BASE,
	solidError: RED.BASE,
};

const StatusWrapper = styled('div')<{
	sizeType: SizeType;
	type: BadgeTypes;
}>(({ sizeType, type }) => ({
	display: 'inline-flex',
	gap: '4px',
	alignItems: 'center',
	borderRadius: '999px',
	textAlign: 'center',
	backgroundColor: backgroundColors[type] ?? BACKGROUND.PRIMARY[60],
	padding: paddings[sizeType],
	color: colors[type] ?? TEXT.PRIMARY,
	minHeight: heights[sizeType],
	fontSize: sizeType === 'sm' ? '12px' : '14px',
	lineHeight: sizeType === 'sm' ? '16px' : '20px',
	div: {
		width: sizeType === 'sm' ? '16px' : '20px',
		height: sizeType === 'sm' ? '16px' : '20px',
	},
}));

export const Badge: FC<{
	className?: string;
	endIcon?: ReactNode;
	label: string;
	onClick?: () => void;
	sizeType: SizeType;
	startIcon?: ReactNode;
	type: BadgeTypes;
}> = ({ label, sizeType, type, startIcon, endIcon, className, onClick }) => (
	<StatusWrapper sizeType={sizeType} type={type} className={className} onClick={onClick}>
		{Boolean(startIcon) && <div>{startIcon}</div>}
		{label}
		{Boolean(endIcon) && <div>{endIcon}</div>}
	</StatusWrapper>
);
